import { useEffect } from 'react';
import { useMutation } from 'react-query';

import { useNotificationStore } from '@/common/store';
import { post } from '@/library/axios';
import type { MutationConfig } from '@/library/react-query';
import type { BaseResponse } from '@/types';
import { displayError } from '@/utils/functions/displayError';

export type GLFHRepaymentType =
  | 'INTEREST_REPAYMENT'
  | 'FULL_REPAYMENT'
  | 'CUSTOM_REPAYMENT'
  | 'CGL_TRANSACTION';

export type ProductShortKeyType = 'DG' | 'GLFH' | 'FX' | 'BP' | 'RP' | 'VP';

export type PaymentMethod =
  | 'CARD'
  | 'NETBANKING'
  | 'APP'
  | 'UPI'
  | 'CREDIT_CARD'
  | 'WALLET'
  | 'DEBIT_CARD';

export type ProductParamType = {
  dg?: {
    phoneNum: string;
    buyLotId: string;
    flowType?: string;
  };
  sip?: {
    startDay: string;
    startDate: string;
    endDate: string;
    paymentCycle: string;
  };
  glfh?: {
    loan_id: string;
    type: GLFHRepaymentType;
  };
  fx?: {
    refId: string;
    fxCustomerId: string;
  };
  rp?: {
    rpId: string;
  };
  bp?: {
    bpId: string;
  };
  vp?: {
    vpId: string;
  };
};
export type OrderReq = {
  amount: number;
  paymentMethod?: PaymentMethod;
  productShortKey: ProductShortKeyType;
  productParams: ProductParamType;
};

export type CreateOrderRes = {
  paymentGatewayOrderId: string;
  amount: number;
  orderStatus: string;
  customerData: {
    customerName: string;
    customerEmail: string;
    customerContactNo: string;
  };
  gatewayData: {
    paymentGatewayName: string;
    productName: string;
    key: string;
  };
  pgSessionId: string;
  paymentRef: string;
  orderId: string;
};

export const createOneTimeOrder = ({
  customerId,
  payload,
}: {
  customerId: string;
  payload: OrderReq;
}): Promise<BaseResponse<CreateOrderRes> | any> => {
  // TODO: extract base url into ENV file
  return post(`/customers/${customerId}/orders?isNewOrderType=true`, payload);
};

type UseCreateOrder = {
  config?: MutationConfig<typeof createOneTimeOrder>;
};

export const useCreateOrder = ({ config }: UseCreateOrder = {}) => {
  const { addNotification } = useNotificationStore();
  const mutation = useMutation({
    onSuccess: () => {
      // data: any
      addNotification({
        type: 'success',
        title: 'Order Initiated',
        message: 'Please complete payment',
      });
    },
    ...config,
    mutationFn: createOneTimeOrder,
  });

  useEffect(() => {
    displayError(mutation.error);
  }, [mutation.error]);

  return mutation;
};
